import { defineResource } from '@tnn/core'
import { z } from 'zod'

export const driverStatuses = ['active', 'inactive'] as const
/** On the truck as its driver, or as a helper (เด็กติดรถ) who loads and unloads */
export const crewKinds = ['driver', 'helper'] as const

export const driver = defineResource({
  app: 'tms',
  name: 'driver',
  path: 'drivers',
  schema: z.object({
    code: z.string().trim().min(1).max(20),
    name: z.string().trim().min(1).max(120),
    kind: z.enum(crewKinds).default('driver'),
    phone: z.string().trim().max(20).nullish(),
    licenseNo: z.string().trim().max(30).nullish(),
    licenseExpiry: z.iso.date().nullish(),
    /** Sign-in account of the driver, so the phone app shows only their jobs */
    userId: z.string().nullish(),
    /** A contract driver (รถร่วม): not on the staff, with their own truck, paid by the trip as `vendorId` */
    contractor: z.boolean().default(false),
    vendorId: z.string().nullish(),
    /** What a trip pays them, unless the job says otherwise */
    tripPay: z.number().min(0).nullish(),
    status: z.enum(driverStatuses).default('active'),
    notes: z.string().max(1000).nullish(),
  }),
  fields: {
    code: { search: true, list: { width: 100, priority: 2 }, form: { span: 4 }, import: { aliases: ['รหัส'] } },
    name: { search: true, list: { priority: 1 }, form: { span: 8 }, import: { aliases: ['ชื่อ', 'ชื่อ-นามสกุล'] } },
    kind: {
      filter: true,
      tones: { driver: 'info', helper: 'neutral' },
      list: { width: 120, priority: 1 },
      form: { span: 4 },
      import: { aliases: ['ตำแหน่ง'] },
    },
    phone: { type: 'phone', search: true, list: { priority: 1 }, form: { span: 4 } },
    licenseNo: { list: { priority: 3 }, form: { span: 4 } },
    licenseExpiry: { list: { priority: 3 }, form: { span: 4 } },
    userId: { type: 'ref', ref: { resource: 'admin.user' }, list: false, form: { span: 6 }, import: false },
    status: { filter: true, tones: { active: 'success', inactive: 'neutral' }, list: { priority: 1 }, form: { span: 6 } },
    contractor: { filter: true, list: { width: 110, priority: 2 }, form: { span: 4, section: 'contract' } },
    vendorId: {
      type: 'ref',
      ref: { resource: 'accounting.customer', where: { status: 'active', isVendor: 'true' } },
      list: false,
      form: { span: 4, section: 'contract' },
      // By the partner's code or name (accounting): import partners before contract drivers
      import: { aliases: ['คู่ค้า', 'ผู้ขาย'] },
    },
    tripPay: { type: 'money', list: { align: 'end', priority: 3, hidden: true }, form: { span: 4, section: 'contract' } },
    notes: { type: 'textarea', form: { span: 12 } },
  },
  title: 'name',
  sections: ['main', 'contract'],
  defaultSort: 'name',
  importKey: 'code',
  card: { title: 'name', subtitle: 'phone', meta: ['code', 'licenseExpiry'], status: 'status' },
})
